import React, { useState } from 'react';
import {
  Box,
  Input,
  Text,
  VStack,
  HStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Modal } from 'src/components/atoms/Modal';
import { ModalBody, ModalFooter } from '@chakra-ui/react';
import { validateEmail } from 'src/utils/email';
import { useInviteUserMutation } from 'src/api/users';
import { useAppContext } from 'src/contexts/AppContext';
import { CustomerRole } from 'src/contexts/AuthContext';
import { Icon } from 'src/components/atoms/Icon';

export const InviteColleaguesStep: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName || '';
  // Per-tenant teammate invite — the customer admin is inviting a user into
  // their own company, so this hits /data/users/invite (isCustomerAdmin gated)
  // rather than the staff-only /data/signup/send-invite-email.
  const inviteUserMutation = useInviteUserMutation(customerId);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (error) {
      setError('');
    }
  };

  const handleSendInvitation = () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setError(
        'This email address does not exist. Please make sure you have entered your email address correctly.',
      );
      return;
    }

    inviteUserMutation.mutate(
      { email: email.trim(), role: CustomerRole.USER },
      {
        onSuccess: () => {
          onOpen();
          setEmail('');
          setError('');
        },
        onError: (mutationError) => {
          const apiMessage =
            mutationError &&
            typeof mutationError === 'object' &&
            'response' in mutationError
              ? (
                  mutationError as {
                    response?: { data?: { message?: string } };
                  }
                ).response?.data?.message
              : undefined;
          setError(
            apiMessage || 'Failed to send invitation. Please try again.',
          );
        },
      },
    );
  };

  const isLoading = inviteUserMutation.isLoading;

  return (
    <Box w="100%">
      <VStack spacing={6} align="stretch">
        {/* Email Input Section */}
        <VStack spacing={4} align="stretch">
          <HStack spacing={4} align="flex-start">
            <Box flex="1">
              <Input
                value={email}
                onChange={handleEmailChange}
                placeholder="teammate's email"
                bg="surface.panel"
                border={'1px solid'}
                borderColor={error ? 'border.red' : 'surface.border'}
                borderRadius="md"
                outline="none"
                px={4}
                py={3}
                fontSize="16px"
                h="48px"
                isInvalid={!!error}
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: error ? 'border.red' : 'icon.green',
                  boxShadow: error
                    ? 'none'
                    : '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
                _invalid={{
                  boxShadow: 'none !important',
                }}
              />
              {error && (
                <Text
                  color="border.red"
                  fontSize="sm"
                  mt={2}
                  fontFamily="marketingPage">
                  {error}
                </Text>
              )}
            </Box>

            <Button
              onClick={handleSendInvitation}
              isLoading={isLoading}
              loadingText="Sending..."
              bg={error ? 'gray.300' : 'icon.green'}
              color="white"
              px={4}
              py={3}
              borderRadius="md"
              fontSize="md"
              fontFamily="marketingPage"
              fontWeight="medium"
              w="180px"
              h="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              isDisabled={!!error}
              _hover={{
                bg: error ? 'gray.300' : 'icon.green',
                opacity: error ? 1 : 0.9,
              }}
              _active={{
                bg: error ? 'gray.300' : 'icon.green',
                opacity: error ? 1 : 0.8,
              }}
              _disabled={{
                bg: 'gray.300',
                color: 'white',
                cursor: 'not-allowed',
                opacity: 1,
              }}
              buttonName="send-invitation">
              Send invitation
            </Button>
          </HStack>
        </VStack>

        {/* Success Modal */}
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          header=""
          maxWidth="588px"
          closeOnOutsideClick={false}>
          <ModalBody pt={9} px={9}>
            <VStack spacing={4} align="stretch">
              <Text
                fontSize="40px"
                fontWeight="bold"
                color="text.light.highContrast"
                textAlign="center"
                fontFamily="marketingPage">
                Invitation successfully sent
              </Text>
              <Text
                color="text.light.secondary"
                fontSize="md"
                textAlign="center"
                fontFamily="marketingPage">
                Your colleague has received an invitation by email. To join,
                they need to confirm it in their email.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter px={9} pb={9}>
            <Button
              onClick={onClose}
              bg="icon.green"
              h="48px"
              color="white"
              px={3}
              py={4}
              borderRadius="md"
              fontSize="md"
              fontWeight="medium"
              fontFamily="marketingPage"
              lineHeight={'24px'}
              w="100%"
              _hover={{
                bg: 'icon.green',
                opacity: 0.9,
              }}
              _active={{
                bg: 'icon.green',
                opacity: 0.8,
              }}
              buttonName="modal-ok">
              Ok
            </Button>
          </ModalFooter>
        </Modal>
      </VStack>
    </Box>
  );
};
